import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { auth, db } from '../config/firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const UserProfileContext = createContext();

export const UserProfileProvider = ({ children }) => {
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [userAuthorization, setUserAuthorization] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // True when the logged-in employee is set as bölge müdürü (manager) of at
  // least one region in the Regions collection.
  const [isRegionManager, setIsRegionManager] = useState(false);
  // True when the logged-in employee is set as the "Yedek SMO" (backup) of at
  // least one center in the Centers collection.
  const [isCenterSmoBackup, setIsCenterSmoBackup] = useState(false);
  // Per-employee, admin-managed page grants (list of menu paths) — a database
  // driven extension of the hardcoded SPECIAL_EMAIL_ACCESS map.
  const [pageAccess, setPageAccess] = useState([]);

  // Create a memoized fetch function that we can call when needed
  const fetchEmployeeData = useCallback(async (email) => {
    if (!email) return;
    
    setIsLoading(true);
    try {
      const employeesRef = collection(db, 'Employees');
      const q = query(employeesRef, where('eMail', '==', email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id;
        const data = querySnapshot.docs[0].data();
        setEmployeeData(data);
        // If no authorization field exists, default to 'employee' role
        setUserAuthorization(data.authorization && data.authorization.length > 0 ? data.authorization : ['employee']);
        setPageAccess(Array.isArray(data.pageAccess) ? data.pageAccess : []);
        setEmployeeId(docId);
        if (data.profileImageUrl) {
          setProfileImageUrl(data.profileImageUrl);
        }

        // Check whether this employee is a bölge müdürü of any region, or the
        // backup (Yedek SMO) of any center — both unlock report views.
        try {
          const [regionSnapshot, centerBackupSnapshot] = await Promise.all([
            getDocs(collection(db, 'Regions')),
            getDocs(query(collection(db, 'Centers'), where('SMOBackup', '==', docId))),
          ]);
          const isResponsibleForRegion = regionSnapshot.docs.some((regionDoc) => {
            const region = regionDoc.data();
            if (region.managerId === docId) return true;
            return Object.values(region.cityManagerIds || {}).some((managerIds) =>
              Array.isArray(managerIds)
                ? managerIds.includes(docId)
                : managerIds === docId
            );
          });
          setIsRegionManager(isResponsibleForRegion);
          setIsCenterSmoBackup(!centerBackupSnapshot.empty);
        } catch (regionError) {
          console.error("Error checking region/backup status:", regionError);
          setIsRegionManager(false);
          setIsCenterSmoBackup(false);
        }
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to manually refresh profile data
  const refetchProfileData = useCallback(() => {
    if (auth.currentUser) {
      fetchEmployeeData(auth.currentUser.email);
    }
  }, [fetchEmployeeData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserEmail(user.email);
        fetchEmployeeData(user.email);
      } else {
        setProfileImageUrl(null);
        setEmployeeData(null);
        setUserAuthorization([]);
        setEmployeeId(null);
        setUserEmail(null);
        setIsRegionManager(false);
        setIsCenterSmoBackup(false);
        setPageAccess([]);
        setIsLoading(false);
      }
    });

    // Initial fetch
    if (auth.currentUser) {
      fetchEmployeeData(auth.currentUser.email);
    }

    return () => unsubscribe();
  }, [fetchEmployeeData]);

  const contextValue = useMemo(() => ({
    profileImageUrl,
    setProfileImageUrl,
    employeeData,
    setEmployeeData,
    userAuthorization,
    setUserAuthorization,
    employeeId,
    userEmail,
    isLoading,
    isRegionManager,
    isCenterSmoBackup,
    pageAccess,
    refetchProfileData
  }), [profileImageUrl, employeeData, userAuthorization, employeeId, userEmail, isLoading, isRegionManager, isCenterSmoBackup, pageAccess, refetchProfileData]);

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => useContext(UserProfileContext);
